import { CheckCircle2, ClipboardList, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ValidationAlert } from './AIConfigurationUI.tsx';
import { SectionCard } from './FormPromptControls.tsx';
import { ReviewSummaryCard } from './ReviewSummary.tsx';
import type { ReviewSummaryItem, ReviewSummaryStatus } from './ReviewSummary.tsx';
import type { AIConfigurationStepId } from '../../configuration/steps.ts';

export type ReviewStepSummary = {
  id: AIConfigurationStepId;
  title: string;
  icon: LucideIcon;
  status: ReviewSummaryStatus;
  items: ReviewSummaryItem[];
};

type ReviewStepProps = {
  activeCapabilityTitles: string[];
  reviewAlerts: Array<[string, string]>;
  reviewWarnings: string[];
  hasUnsavedPromptChanges: boolean;
  reviewSummaries: ReviewStepSummary[];
  onOpenConversationTest: () => void;
  onReviewFix: (fieldKey: string) => void;
  onReviewEdit: (stepId: AIConfigurationStepId) => void;
};

const ReviewStep = ({
  activeCapabilityTitles,
  reviewAlerts,
  reviewWarnings,
  hasUnsavedPromptChanges,
  reviewSummaries,
  onOpenConversationTest,
  onReviewFix,
  onReviewEdit,
}: ReviewStepProps) => (
  <>
    <SectionCard
      title="A IA poderá:"
      icon={CheckCircle2}
      description="Este é o atendimento que será ativado quando você publicar."
    >
      {reviewAlerts.length === 0 && (
        <div className="ai-review-ready" role="status">
          <div className="ai-review-ready-copy">
            <CheckCircle2 size={20} />
            <div>
              <strong>Tudo pronto para publicar</strong>
              <span>Revise o resumo abaixo e clique em “Publicar atendimento”.</span>
            </div>
          </div>
          <button
            type="button"
            className="ai-button is-test"
            onClick={onOpenConversationTest}
          >
            <MessageSquare size={19} /> Testar conversa
          </button>
        </div>
      )}
      <ul className="grid gap-2 sm:grid-cols-2">
        {activeCapabilityTitles.map((title) => (
          <li key={title} className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            <CheckCircle2 size={16} /> {title}
          </li>
        ))}
      </ul>
      {(reviewAlerts.length > 0 || reviewWarnings.length > 0) && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-slate-900">O que precisa de atenção</h3>
          {reviewAlerts.map(([fieldKey, message]) => (
            <ValidationAlert key={fieldKey} message={message} onFix={() => onReviewFix(fieldKey)} />
          ))}
          {reviewWarnings.map((warning) => (
            <p key={warning} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{warning}</p>
          ))}
        </div>
      )}
      {hasUnsavedPromptChanges && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
          Ao publicar, estas alterações passam a valer no atendimento.
        </p>
      )}
    </SectionCard>

    <SectionCard
      title="Resumo da configuração"
      icon={ClipboardList}
      description="Estes são os dados que a IA vai usar nas conversas."
      hint="Confira cada etapa e use “Editar” para ajustar o que precisar."
    >
      <div className="ai-review-grid">
        {reviewSummaries.map((summary) => (
          <ReviewSummaryCard
            key={summary.id}
            icon={summary.icon}
            title={summary.title}
            status={summary.status}
            items={summary.items}
            onEdit={() => onReviewEdit(summary.id)}
          />
        ))}
      </div>
    </SectionCard>
  </>
);

export default ReviewStep;
